import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';

import Input from '../../components/ui/Input';
import {postsService} from '../../services/postsService';
import HeroImageUpload from './components/HeroImageUpload';
import InContentImageUpload from './components/InContentImageUpload';
import PublishingControls from './components/PublishingControls';
import AutosaveIndicator from './components/AutosaveIndicator';
import ContentEditor from "./components/ContentEditor";
import BlogPostDetail from "../blog-post-detail";

const AdminPostEditor = () => {
  const {id: postIdFromUrl} = useParams();
  const navigate = useNavigate();

  // Support both URL params and navigation state for backward compatibility
  const postId = postIdFromUrl;

  // Check if this is a new post creation (id === 'new' or path is /admin/posts/new)
  const isNewPost = postId === 'new' || !postId;

  const [isLoadingPost, setIsLoadingPost] = useState(!isNewPost && !!postId);
  const [loadError, setLoadError] = useState('');
  const [editingPost, setEditingPost] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    heroImage: null,
    status: 'draft'
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSlug, setAutoSlug] = useState(true);
  const [saveError, setSaveError] = useState('');

  // Fetch post data if postId is provided
  useEffect(() => {
    const fetchPost = async () => {
      // Skip fetch for new post creation
      if (isNewPost) {
        setIsLoadingPost(false);
        return;
      }

      // Validate postId before making API call
      if (!postId || postId === ':id') {
        setLoadError('Invalid post ID. Please select a post from the posts list.');
        setIsLoadingPost(false);
        return;
      }

      if (!postId) return;

      try {
        setIsLoadingPost(true);
        setLoadError('');
        const post = await postsService?.getPostById(postId);
        setEditingPost(post);

        // Initialize form with fetched post data - hero_image_url is a STRING
        setFormData({
          title: post?.title || '',
          slug: post?.slug || '',
          excerpt: post?.excerpt || '',
          content: post?.content || '',
          heroImage: post?.heroImage || '', // STRING URL from database
          heroImageAlt: post?.heroImageAlt || '',
          status: post?.status || 'draft'
        });
        setAutoSlug(false);
      } catch (error) {
        console.error('Failed to fetch post:', error);
        setLoadError(error?.message || 'Failed to load post');
      } finally {
        setIsLoadingPost(false);
      }
    };

    fetchPost();
  }, [postId, isNewPost]);

  useEffect(() => {
    if (autoSlug && formData?.title) {
      const slug = formData?.title?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-')?.replace(/^-+|-+$/g, '');
      setFormData(prev => ({...prev, slug}));
    }
  }, [formData?.title, autoSlug]);

  useEffect(() => {
    const autosaveInterval = setInterval(() => {
      if (formData?.title || formData?.content) {
        handleAutoSave();
      }
    }, 30000);

    return () => clearInterval(autosaveInterval);
  }, [formData]);

  const handleAutoSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      const now = new Date();
      setLastSaved(now?.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }));
      setIsSaving(false);
    }, 1000);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData?.slug?.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/?.test(formData?.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }

    if (!formData?.excerpt?.trim()) {
      newErrors.excerpt = 'Excerpt is required';
    } else if (formData?.excerpt?.length > 200) {
      newErrors.excerpt = 'Excerpt must be less than 200 characters';
    }

    if (!formData?.content?.trim()) {
      newErrors.content = 'Content is required';
    }

    if (!formData?.heroImage) {
      newErrors.heroImage = 'Hero image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async (status) => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      const postData = {
        title: formData?.title,
        slug: formData?.slug,
        excerpt: formData?.excerpt,
        content: formData?.content,
        heroImageUrl: formData?.heroImage || null, // STRING URL
        heroImageAlt: formData?.heroImageAlt || null,
        status: status
      };

      let savedPost;
      if (editingPost?.id) {
        savedPost = await postsService?.updatePost(editingPost?.id, postData);
      } else {
        savedPost = await postsService?.createPost(postData);
      }

      // Update local state with saved post data - hero_image_url is a STRING
      if (savedPost) {
        setEditingPost(savedPost);
        setFormData(prev => ({
          ...prev,
          heroImage: savedPost?.heroImage || prev?.heroImage, // STRING URL
          heroImageAlt: savedPost?.heroImageAlt || prev?.heroImageAlt
        }));
      }

      const now = new Date();
      setLastSaved(now?.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }));

      setTimeout(() => {
        navigate('/admin-posts-management');
      }, 500);
    } catch (error) {
      setSaveError(error?.message || 'Failed to save post');
      console.error('Save post error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = () => handleSave('published');
  const handleSaveDraft = () => handleSave('draft');

  const handleCancel = () => {
    if (formData?.title || formData?.content) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/admin-posts-management');
      }
    } else {
      navigate('/admin-posts-management');
    }
  };

  const handleImageInsert = (image) => {
    const imageMarkdown = `\n\n![${image?.alt}](${image?.url})\n\n`;
    setFormData(prev => ({
      ...prev,
      content: prev?.content + imageMarkdown
    }));
  };

  const breadcrumbItems = [
    {label: 'Admin', path: '/admin-dashboard'},
    {label: 'Posts', path: '/admin-posts-management'},
    {label: editingPost ? 'Edit Post' : 'New Post', path: '/admin-post-editor'}
  ];

  // Show loading state while fetching post
  if (isLoadingPost) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation/>
        <div className="content-area">
          <div className="max-w-7xl mx-auto py-6 md:py-8">
            <div className="flex items-center justify-center h-64">
              <p className="text-muted-foreground">Loading post...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if post failed to load
  if (loadError) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation/>
        <div className="content-area">
          <div className="max-w-7xl mx-auto py-6 md:py-8">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-destructive mb-2">Failed to Load Post</h2>
              <p className="text-sm text-muted-foreground mb-4">{loadError}</p>
              <button
                onClick={() => navigate('/admin-posts-management')}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Back to Posts
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation/>
      <div className="content-area">
        <div className="max-w-7xl mx-auto py-6 md:py-8">
          <BreadcrumbNavigation customItems={breadcrumbItems}/>

          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {editingPost ? 'Edit Post' : 'Create New Post'}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {editingPost
                  ? 'Update your blog post content and settings' : 'Write and publish a new blog post for your audience'
                }
              </p>
            </div>
            <AutosaveIndicator isSaving={isSaving} lastSaved={lastSaved}/>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              <div className="bg-card border border-border rounded-lg p-4 md:p-6 space-y-4 md:space-y-6">
                <Input
                  label="Post Title"
                  type="text"
                  placeholder="Enter your post title"
                  value={formData?.title}
                  onChange={(e) => setFormData(prev => ({...prev, title: e?.target?.value}))}
                  error={errors?.title}
                  required
                />

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">
                      URL Slug
                    </label>
                    <button
                      onClick={() => setAutoSlug(!autoSlug)}
                      className="text-xs text-primary hover:underline"
                    >
                      {autoSlug ? 'Edit manually' : 'Auto-generate'}
                    </button>
                  </div>
                  <Input
                    type="text"
                    placeholder="post-url-slug"
                    value={formData?.slug}
                    onChange={(e) => {
                      setAutoSlug(false);
                      setFormData(prev => ({...prev, slug: e?.target?.value}));
                    }}
                    error={errors?.slug}
                    description="URL-friendly version of the title"
                    required
                    disabled={autoSlug}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Excerpt <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    value={formData?.excerpt}
                    onChange={(e) => setFormData(prev => ({...prev, excerpt: e?.target?.value}))}
                    placeholder="Write a brief summary of your post (max 200 characters)"
                    maxLength={200}
                    className="w-full min-h-[100px] px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                  />
                  <div className="flex items-center justify-between text-xs">
                    {errors?.excerpt && (
                      <span className="text-destructive">{errors?.excerpt}</span>
                    )}
                    <span className="text-muted-foreground ml-auto">
                      {formData?.excerpt?.length}/200
                    </span>
                  </div>
                </div>
              </div>

              <HeroImageUpload
                heroImage={formData?.heroImage}
                heroImageAlt={formData?.heroImageAlt}
                onImageUpload={(imageUrl, altText) => {
                  console.log('[Editor] Hero image updated - URL:', imageUrl);
                  setFormData(prev => ({
                    ...prev,
                    heroImage: imageUrl, // STRING URL
                    heroImageAlt: altText || prev?.heroImageAlt
                  }));
                }}
                onImageRemove={() => setFormData(prev => ({
                  ...prev,
                  heroImage: '',
                  heroImageAlt: ''
                }))}
                postId={editingPost?.id}
              />
              {errors?.heroImage && (
                <p className="text-sm text-destructive -mt-4">{errors?.heroImage}</p>
              )}

              <ContentEditor
                content={formData?.content}
                onChange={(content) => setFormData(prev => ({...prev, content}))}
              />
              {errors?.content && (
                <p className="text-sm text-destructive -mt-4">{errors?.content}</p>
              )}

              <InContentImageUpload onImageInsert={handleImageInsert} postId={editingPost?.id}/>
            </div>

            <div className="lg:col-span-1">
              <PublishingControls
                status={formData?.status}
                onStatusChange={(status) => setFormData(prev => ({...prev, status}))}
                onSave={handleSaveDraft}
                onPublish={handlePublish}
                onCancel={handleCancel}
                isSaving={isSaving}
                lastSaved={lastSaved}
              />
              {saveError && (
                <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive">{saveError}</p>
                </div>
              )}
            </div>


          </div>
          {/* Divider */}
          <div className="relative my-20">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"/>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground"></span>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 md:p-6 space-y-4">
            <h2 className="text-xl font-bold mb-4 text-foreground">Live Article Preview</h2>
            <BlogPostDetail post={formData}/>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminPostEditor;